import { create } from 'zustand';
import { authApi } from '@/services/endpoints/auth';
import { setSessionExpiredHandler } from '@/services/apiClient';
import { tokenStore } from '@/services/tokenStore';
import type { AuthUser } from '@/domain/types';

interface AuthState {
  user: AuthUser | null;
  /** True until the initial "am I still signed in?" check has finished. */
  isInitialising: boolean;
  isAuthenticated: boolean;

  initialise: () => Promise<void>;
  login: (username: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isInitialising: true,
  isAuthenticated: false,

  /**
   * Runs once at startup. A stored token is not proof of a live session — the
   * account may have been deactivated since — so it is verified against the
   * server rather than trusted.
   */
  async initialise() {
    if (!tokenStore.getAccess() && !tokenStore.getRefresh()) {
      set({ isInitialising: false, isAuthenticated: false, user: null });
      return;
    }

    try {
      const user = await authApi.me();
      set({ user, isAuthenticated: true, isInitialising: false });
    } catch {
      tokenStore.clear();
      set({ user: null, isAuthenticated: false, isInitialising: false });
    }
  },

  async login(username, password) {
    const user = await authApi.login(username, password);
    set({ user, isAuthenticated: true });
    return user;
  },

  async logout() {
    await authApi.logout();
    set({ user: null, isAuthenticated: false });
  },

  setUser(user) {
    set({ user, isAuthenticated: user !== null });
  },
}));

/**
 * Wires the API client's session-expiry callback into the store, so a failed
 * token refresh anywhere in the app drops the user to the login screen instead
 * of leaving a shell rendering 401s.
 */
setSessionExpiredHandler(() => {
  useAuthStore.setState({ user: null, isAuthenticated: false });
});

/** Convenience selectors — components subscribe to one field, not the store. */
export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
